import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { GripVertical, Check, GripHorizontal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAV_TABS } from "@/lib/navTabs";

const SHOW = ["/", "/dashboard", "/snapbudget", "/tax-hub"];
const TO = {
  briefing: "/", personal: "/dashboard?mode=personal", business: "/dashboard?mode=business",
  snapbudget: "/snapbudget", tax: "/tax-hub",
};
const ORDER_KEY = "mobile_tab_order";
const TAB_BY_KEY = Object.fromEntries(NAV_TABS.map((t) => [t.key, t]));

const readOrder = () => {
  try {
    const r = JSON.parse(localStorage.getItem(ORDER_KEY));
    if (Array.isArray(r)) {
      const valid = r.filter((k) => TAB_BY_KEY[k]);
      NAV_TABS.forEach((t) => { if (!valid.includes(t.key)) valid.push(t.key); });
      return valid;
    }
  } catch { /* ignore */ }
  return NAV_TABS.map((t) => t.key);
};

export default function MobileTabBar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [sp] = useSearchParams();
  const [order, setOrder] = useState(readOrder);
  const [editing, setEditing] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [showHint, setShowHint] = useState(() => { try { return !localStorage.getItem("mtab_hint_seen"); } catch { return false; } });
  const holdTimer = useRef(null);
  const listRef = useRef(null);

  const visible = !!user && user.role !== "crew" && SHOW.includes(loc.pathname);

  useEffect(() => {
    document.body.classList.toggle("has-bottom-nav", visible);
    return () => document.body.classList.remove("has-bottom-nav");
  }, [visible]);

  useEffect(() => { if (!visible) setEditing(false); }, [visible]);

  const dismissHint = () => { setShowHint(false); try { localStorage.setItem("mtab_hint_seen", "1"); } catch { /* ignore */ } };
  useEffect(() => { if (editing) dismissHint(); }, [editing]);
  useEffect(() => {
    if (!visible || !showHint) return;
    const t = setTimeout(dismissHint, 7000);
    return () => clearTimeout(t);
  }, [visible, showHint]);

  if (!visible) return null;

  const mode = sp.get("mode") === "business" ? "business" : "personal";
  const activeKey =
    loc.pathname === "/" ? "briefing"
    : loc.pathname.startsWith("/dashboard") ? mode
    : loc.pathname.startsWith("/snapbudget") ? "snapbudget"
    : loc.pathname.startsWith("/tax-hub") ? "tax" : "";

  const ordered = order.map((k) => TAB_BY_KEY[k]).filter(Boolean);
  const persist = (next) => { try { localStorage.setItem(ORDER_KEY, JSON.stringify(next)); } catch { /* ignore */ } };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next); persist(next);
  };

  const startHold = () => { holdTimer.current = setTimeout(() => setEditing(true), 550); };
  const cancelHold = () => { if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; } };

  // Pointer-based drag reordering inside the sheet
  const onDragMove = (e) => {
    if (!dragKey || !listRef.current) return;
    const rows = [...listRef.current.querySelectorAll("[data-row]")];
    const y = e.clientY;
    let target = rows.length - 1;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      if (y < r.top + r.height / 2) { target = i; break; }
    }
    const cur = order.indexOf(dragKey);
    if (target >= 0 && target !== cur) {
      const next = [...order];
      next.splice(cur, 1);
      next.splice(target, 0, dragKey);
      setOrder(next);
    }
  };
  const endDrag = () => { if (dragKey) { persist(order); setDragKey(null); } };

  return (
    <>
      {showHint && (
        <div data-testid="mtab-hint" className="lg:hidden fixed bottom-[4.25rem] inset-x-0 z-[45] flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-2 bg-emerald-500 text-[#04120c] text-xs font-medium px-3 py-2 rounded-full shadow-lg">
            <GripHorizontal className="w-3.5 h-3.5" /> Long-press a tab to rearrange your bar
            <button data-testid="mtab-hint-dismiss" onClick={dismissHint} className="ml-1 font-semibold underline">Got it</button>
          </div>
        </div>
      )}

      <nav
        data-testid="mobile-tab-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0a1310]/95 backdrop-blur-md border-t border-emerald-500/15 flex"
      >
        {ordered.map((t) => {
          const on = activeKey === t.key;
          return (
            <button
              key={t.key}
              data-testid={`mtab-${t.key}`}
              onClick={() => navigate(TO[t.key])}
              onPointerDown={startHold}
              onPointerUp={cancelHold}
              onPointerLeave={cancelHold}
              onContextMenu={(e) => { e.preventDefault(); setEditing(true); }}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-medium transition-colors ${on ? t.mActive : "text-slate-400 hover:text-slate-200"}`}
            >
              <span className={`grid place-items-center w-8 h-8 rounded-full transition-colors ${on ? t.mBg : ""}`}>
                <t.Icon className="w-4 h-4" />
              </span>
              {t.short}
            </button>
          );
        })}
      </nav>

      {editing && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end" data-testid="mtab-reorder-sheet">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditing(false)} />
          <div className="relative w-full bg-[#0a1310] border-t border-emerald-500/20 rounded-t-2xl p-4 pb-6 text-slate-100 shadow-2xl">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold tracking-wide text-emerald-300 flex items-center gap-2"><GripHorizontal className="w-4 h-4" /> Reorder tabs</span>
              <button data-testid="mtab-reorder-done" onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-emerald-500 text-[#04120c] text-xs font-semibold">
                <Check className="w-4 h-4" /> Done
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">Drag the handle to rearrange. The first tab is pinned to the far left.</p>
            <ul ref={listRef} onPointerMove={onDragMove} onPointerUp={endDrag} onPointerLeave={endDrag} className="space-y-2">
              {ordered.map((t, i) => (
                <li
                  key={t.key}
                  data-row
                  data-testid={`mtab-reorder-${t.key}`}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 select-none transition-colors ${dragKey === t.key ? "border-emerald-400 bg-emerald-500/10" : "border-white/10 bg-white/[0.03]"}`}
                >
                  <button
                    data-testid={`mtab-drag-${t.key}`}
                    onPointerDown={(e) => { e.preventDefault(); setDragKey(t.key); }}
                    className="touch-none cursor-grab active:cursor-grabbing text-slate-400 hover:text-emerald-300 -ml-1 p-1"
                    aria-label={`Drag ${t.label}`}
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                  <span className={`grid place-items-center w-8 h-8 rounded-full ${t.mBg}`}><t.Icon className="w-4 h-4" /></span>
                  <span className="text-sm flex-1">{t.label}{i === 0 && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-400">Pinned</span>}</span>
                  <button data-testid={`mtab-move-up-${t.key}`} onClick={() => move(i, -1)} disabled={i === 0} className="w-8 h-8 grid place-items-center rounded-lg border border-white/10 disabled:opacity-30 hover:border-emerald-400 transition-colors text-xs">↑</button>
                  <button data-testid={`mtab-move-down-${t.key}`} onClick={() => move(i, 1)} disabled={i === ordered.length - 1} className="w-8 h-8 grid place-items-center rounded-lg border border-white/10 disabled:opacity-30 hover:border-emerald-400 transition-colors text-xs">↓</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
